import React, { useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Typography,
  Button,
  TextField,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { CustomTable } from "../../components/CustomTable/CustomTable";
import { MainCard } from "../UserAccessManagement/UserAccessManagement.style";
import { ModalOverlay, ModalContainer } from "../../components/Modal/Modal.styled";
import Modal from "../../components/Modal/Modal";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal";
import { useLoading } from "../../contexts/LoadingContext";
import { useRbac } from "../../hooks/RbacContext";
import { BUSINESS_SEMANTIC_LAYER_CONCEPT_TYPE_OPTIONS } from "../../enum/enums";
import { useBusinessDefinitionViewModel } from "./useBusinessDefinitionViewModel";

const THEME = { primary: "rgb(44, 52, 123)", primaryRgba: (a) => `rgba(44, 52, 123, ${a})` };

const BusinessDefinition = () => {
  const selectedDbId =
    typeof window !== "undefined"
      ? Number(localStorage.getItem("selected_user_db_id")) || null
      : null;

  const { showLoading, hideLoading } = useLoading();
  const { checkAccess } = useRbac();
  const { rows, loading, error, addConcept, updateConcept, deleteConcept } =
    useBusinessDefinitionViewModel(selectedDbId);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addConceptType, setAddConceptType] = useState("");
  const [addConceptKey, setAddConceptKey] = useState("");
  const [addDescription, setAddDescription] = useState("");
  const [addDefinition, setAddDefinition] = useState("{}");

  const [editModalRow, setEditModalRow] = useState(null);
  const [editConceptType, setEditConceptType] = useState("");
  const [editConceptKey, setEditConceptKey] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editDefinition, setEditDefinition] = useState("");
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [successModal, setSuccessModal] = useState(null);

  const columns = [
    { id: "concept_type", label: "Type", align: "left" },
    { id: "concept_key", label: "Key", align: "left" },
    { id: "concept_text", label: "Description", align: "left" },
    { id: "created_on", label: "Created On", align: "left" },
    { id: "updated_on", label: "Updated On", align: "left" },
  ];

  const handleOpenEdit = (row) => {
    setEditModalRow(row);
    setEditConceptType(row.concept_type || "");
    setEditConceptKey(row.concept_key || "");
    setEditDescription(row.concept_text || "");
    setEditDefinition(
      row.definition != null
        ? (typeof row.definition === "string"
          ? row.definition
          : JSON.stringify(row.definition, null, 2))
        : "{}"
    );
  };

  const handleCloseEdit = () => {
    setEditModalRow(null);
    setEditConceptType("");
    setEditConceptKey("");
    setEditDescription("");
    setEditDefinition("");
  };

  const handleOpenAdd = () => {
    setAddConceptType(BUSINESS_SEMANTIC_LAYER_CONCEPT_TYPE_OPTIONS[0]?.value ?? "metric");
    setAddConceptKey("");
    setAddDescription("");
    setAddDefinition("{}");
    setAddModalOpen(true);
  };

  const handleCloseAdd = () => {
    setAddModalOpen(false);
    setAddConceptType("");
    setAddConceptKey("");
    setAddDescription("");
    setAddDefinition("{}");
  };

  const handleAddSave = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!selectedDbId) return;
    const trimmedKey = addConceptKey.trim();
    const trimmedDesc = addDescription.trim();
    const trimmedDef = addDefinition.trim() || "{}";
    if (!trimmedKey) {
      setSuccessModal({
        type: "error",
        title: "Validation",
        message: "Key cannot be empty",
      });
      return;
    }
    let definitionValue;
    try {
      definitionValue = JSON.parse(trimmedDef);
    } catch {
      setSuccessModal({
        type: "error",
        title: "Validation",
        message: "Definition must be valid JSON",
      });
      return;
    }
    try {
      showLoading();
      const result = await addConcept({
        userDbId: selectedDbId,
        conceptType: addConceptType || "metric",
        conceptKey: trimmedKey,
        conceptText: trimmedDesc || "",
        definition: definitionValue,
      });
      if (result.success) {
        handleCloseAdd();
        setSuccessModal({
          type: "success",
          title: "Success",
          message: "Business definition added successfully",
        });
      } else {
        setSuccessModal({
          type: "error",
          title: "Add Failed",
          message: result.error || "Failed to add business definition",
        });
      }
    } catch (err) {
      setSuccessModal({
        type: "error",
        title: "Add Failed",
        message: err.message || "Failed to add business definition",
      });
    } finally {
      hideLoading();
    }
  };

  const handleEditSave = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!editModalRow) return;
    const trimmedDesc = editDescription.trim();
    const trimmedKey = editConceptKey.trim();
    const trimmedDef = editDefinition.trim() || "{}";
    if (!trimmedKey) {
      setSuccessModal({
        type: "error",
        title: "Validation",
        message: "Key cannot be empty",
      });
      return;
    }
    let definitionValue;
    try {
      definitionValue = JSON.parse(trimmedDef);
    } catch {
      setSuccessModal({
        type: "error",
        title: "Validation",
        message: "Definition must be valid JSON",
      });
      return;
    }
    try {
      showLoading();
      const result = await updateConcept({
        businessSemanticLayerId: editModalRow.id,
        conceptType: editConceptType || null,
        conceptKey: trimmedKey,
        conceptText: trimmedDesc || null,
        definition: definitionValue,
      });
      if (result.success) {
        handleCloseEdit();
        setSuccessModal({
          type: "success",
          title: "Success",
          message: "Business definition updated successfully",
        });
      } else {
        setSuccessModal({
          type: "error",
          title: "Update Failed",
          message: result.error || "Failed to update business definition",
        });
      }
    } catch (e) {
      setSuccessModal({
        type: "error",
        title: "Update Failed",
        message: e.message || "Failed to update business definition",
      });
    } finally {
      hideLoading();
    }
  };

  const handleDeleteClick = (row) => setDeleteConfirmation({ row, open: true });

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmation?.row) return;
    const { row } = deleteConfirmation;
    try {
      showLoading();
      const result = await deleteConcept(row.id);
      setDeleteConfirmation(null);
      if (result.success) {
        setSuccessModal({
          type: "success",
          title: "Success",
          message: "Business definition deleted successfully",
        });
      } else {
        setSuccessModal({
          type: "error",
          title: "Delete Failed",
          message: result.error || "Failed to delete business definition",
        });
      }
    } catch (e) {
      setDeleteConfirmation(null);
      setSuccessModal({
        type: "error",
        title: "Delete Failed",
        message: e.message || "Failed to delete business definition",
      });
    } finally {
      hideLoading();
    }
  };

  const getActions = (row) => {
    const actions = [];
    if (checkAccess("businessDefinition", "update")) {
      actions.push({
        icon: <EditIcon fontSize="small" />,
        label: "Edit",
        onClick: () => handleOpenEdit(row),
        sx: {
          color: THEME.primary,
          "&:hover": { backgroundColor: THEME.primaryRgba(0.08) },
        },
      });
    }
    if (checkAccess("businessDefinition", "delete")) {
      actions.push({
        icon: <DeleteIcon fontSize="small" />,
        label: "Delete",
        onClick: () => handleDeleteClick(row),
        sx: {
          color: "#b91c1c",
          "&:hover": { backgroundColor: "rgba(220, 38, 38, 0.08)" },
        },
      });
    }
    return actions;
  };

  if (!selectedDbId) {
    return (
      <MainCard sx={{ height: "calc(100vh - 72px)", maxHeight: "calc(100vh - 72px)" }}>
        <Box sx={{ p: 3 }}>
          <Typography variant="body2" sx={{ color: "#64748b" }}>
            Please select a database first to view business definitions.
          </Typography>
        </Box>
      </MainCard>
    );
  }

  if (loading) {
    return (
      <MainCard sx={{ height: "calc(100vh - 72px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          <CircularProgress size={40} sx={{ color: "rgb(44, 52, 123)" }} />
          <Typography variant="body2" sx={{ color: "#64748b" }}>
            Loading business definitions…
          </Typography>
        </Box>
      </MainCard>
    );
  }

  if (error) {
    return (
      <MainCard sx={{ p: 1.5 }}>
        <Alert severity="error" sx={{ borderRadius: 8 }}>
          {error}
        </Alert>
      </MainCard>
    );
  }

  if (!checkAccess("businessDefinition", "view")) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          You don&apos;t have permission to view Business Definition.
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <MainCard sx={{ height: "calc(100vh - 72px)", maxHeight: "calc(100vh - 72px)" }}>
        <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <CustomTable
            columns={columns}
            data={rows}
            showActions={true}
            showSearch
            showColumnVisibility={false}
            showAddButton={checkAccess("businessDefinition", "add")}
            onAdd={handleOpenAdd}
            addButtonText="Add Business Definition"
            addButtonIcon={<AddIcon />}
            pageSize={10}
            stickyHeader
            actions={getActions}
            title="Business Definition"
            subtitle="View semantic concepts configured for this database"
            alignment="left"
            fontSize="12px"
            showFilters
            compact={true}
          />
        </Box>
      </MainCard>

      {/* Add modal - same style as Review SQL */}
      {addModalOpen && (
        <ModalOverlay onClick={handleCloseAdd}>
          <ModalContainer
            onClick={(e) => e.stopPropagation()}
            sx={{ maxWidth: 720, width: "90%", padding: 0, overflow: "hidden", maxHeight: "90vh", display: "flex", flexDirection: "column" }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.5, borderBottom: "1px solid #e2e8f0" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: "1rem" }}>
                Add Business Definition
              </Typography>
              <IconButton size="small" onClick={handleCloseAdd} aria-label="Close">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 1.5, display: "flex", flexDirection: "column", gap: 2 }}>
              <Box>
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Type
                </Typography>
                <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                  <InputLabel id="add-concept-type-label">Type</InputLabel>
                  <Select
                    labelId="add-concept-type-label"
                    label="Type"
                    value={addConceptType}
                    onChange={(e) => setAddConceptType(e.target.value)}
                    sx={{ "& .MuiSelect-select": { fontSize: "0.875rem" } }}
                  >
                    {BUSINESS_SEMANTIC_LAYER_CONCEPT_TYPE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Key
                </Typography>
                <TextField
                  fullWidth
                  value={addConceptKey}
                  onChange={(e) => setAddConceptKey(e.target.value)}
                  variant="outlined"
                  size="small"
                  placeholder="e.g. revenue"
                  sx={{ mt: 0.5, "& .MuiInputBase-root": { fontSize: "0.875rem" } }}
                />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Description
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  value={addDescription}
                  onChange={(e) => setAddDescription(e.target.value)}
                  variant="outlined"
                  size="small"
                  placeholder="Business description of this concept"
                  sx={{ mt: 0.5, "& .MuiInputBase-root": { fontSize: "0.875rem" } }}
                />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Definition (JSON)
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  value={addDefinition}
                  onChange={(e) => setAddDefinition(e.target.value)}
                  variant="outlined"
                  size="small"
                  placeholder='e.g. {"table": "sales", "column": "amount"}'
                  sx={{
                    mt: 0.5,
                    "& .MuiInputBase-root": { fontSize: "0.875rem" },
                    "& textarea": { fontFamily: "monospace" },
                  }}
                />
              </Box>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, px: 2, py: 1.5, borderTop: "1px solid #e2e8f0", backgroundColor: "#fafafa" }}>
              <Button type="button" variant="outlined" size="small" onClick={handleCloseAdd}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="contained"
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleAddSave(e);
                }}
                sx={{ backgroundColor: "rgb(44, 52, 123)", "&:hover": { backgroundColor: "rgb(35, 42, 100)" } }}
              >
                Add
              </Button>
            </Box>
          </ModalContainer>
        </ModalOverlay>
      )}

      {/* Edit modal - same style as Review SQL */}
      {editModalRow && (
        <ModalOverlay onClick={handleCloseEdit}>
          <ModalContainer
            onClick={(e) => e.stopPropagation()}
            sx={{ maxWidth: 720, width: "90%", padding: 0, overflow: "hidden", maxHeight: "90vh", display: "flex", flexDirection: "column" }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.5, borderBottom: "1px solid #e2e8f0" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: "1rem" }}>
                Update Business Definition
              </Typography>
              <IconButton size="small" onClick={handleCloseEdit} aria-label="Close">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 1.5, display: "flex", flexDirection: "column", gap: 2 }}>
              <Box>
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Type
                </Typography>
                <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                  <InputLabel id="edit-concept-type-label">Type</InputLabel>
                  <Select
                    labelId="edit-concept-type-label"
                    label="Type"
                    value={editConceptType}
                    onChange={(e) => setEditConceptType(e.target.value)}
                    sx={{ "& .MuiSelect-select": { fontSize: "0.875rem" } }}
                  >
                    {BUSINESS_SEMANTIC_LAYER_CONCEPT_TYPE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Key
                </Typography>
                <TextField
                  fullWidth
                  value={editConceptKey}
                  onChange={(e) => setEditConceptKey(e.target.value)}
                  variant="outlined"
                  size="small"
                  sx={{ mt: 0.5, "& .MuiInputBase-root": { fontSize: "0.875rem" } }}
                />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Description
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  variant="outlined"
                  size="small"
                  sx={{ mt: 0.5, "& .MuiInputBase-root": { fontSize: "0.875rem" } }}
                />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Definition (JSON)
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  value={editDefinition}
                  onChange={(e) => setEditDefinition(e.target.value)}
                  variant="outlined"
                  size="small"
                  placeholder='e.g. {"table": "sales", "column": "amount"}'
                  sx={{
                    mt: 0.5,
                    "& .MuiInputBase-root": { fontSize: "0.875rem" },
                    "& textarea": { fontFamily: "monospace" },
                  }}
                />
              </Box>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, px: 2, py: 1.5, borderTop: "1px solid #e2e8f0", backgroundColor: "#fafafa" }}>
              <Button type="button" variant="outlined" size="small" onClick={handleCloseEdit}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="contained"
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleEditSave(e);
                }}
                sx={{ backgroundColor: "rgb(44, 52, 123)", "&:hover": { backgroundColor: "rgb(35, 42, 100)" } }}
              >
                Update
              </Button>
            </Box>
          </ModalContainer>
        </ModalOverlay>
      )}

      {/* Delete confirmation modal */}
      {deleteConfirmation && (
        <ConfirmationModal
          open={deleteConfirmation.open}
          title="Delete Business Definition"
          message={`Are you sure you want to delete business definition &quot;${deleteConfirmation.row?.concept_key ?? ""}&quot;?`}
          onClose={() => setDeleteConfirmation(null)}
          onConfirm={handleDeleteConfirm}
          buttonText="Delete"
          colorCode="#b91c1c"
        />
      )}

      {/* Success / error feedback modal */}
      {successModal && (
        <Modal
          type={successModal.type}
          title={successModal.title}
          message={successModal.message}
          onClose={() => setSuccessModal(null)}
        />
      )}
    </>
  );
};

export default BusinessDefinition;
